/**
 * Why a finding sits where it does. The queue order is the priority, so
 * the row does not print a band (the facts behind the score — severity,
 * state, judge — are already on the row): it offers "Why here?", or
 * "Pinned: High" when a teammate pinned it. The trigger opens the stored
 * factor breakdown
 * (`finding.explainPriority`); the detail pane shows the same breakdown
 * inline. Every factor is a sentence, so the order can be defended.
 */
import { forwardRef, type ComponentProps } from "react";
import { ChevronDown } from "lucide-react";
import {
  priorityBand,
  type PriorityBand,
  type PriorityBreakdown,
} from "@iamu/shared";
import { Chip } from "@/components/ui/chip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

/** Words for a manual pin; the stored value is still a band. */
export const PIN_LABEL: Record<PriorityBand, string> = {
  P1: "Fix first",
  P2: "High",
  P3: "Normal",
  P4: "Low",
};

const FACTOR_LABEL: Record<string, string> = {
  impact: "Impact",
  compliance: "Compliance",
  human_test: "Human test",
  reach: "Reach",
  recurrence: "Recurrence",
  regression: "Regression",
  age: "Age",
};

export interface PriorityChipData {
  priorityScore: number | null;
  priorityOverride: string | null;
}

/** "Pinned: High", or null when nobody pinned it. */
export function pinLabel(override: string | null): string | null {
  if (!override) return null;
  return `Pinned: ${PIN_LABEL[override as PriorityBand] ?? override}`;
}

export const PriorityChipButton = forwardRef<
  HTMLButtonElement,
  ComponentProps<"button"> & { priority: PriorityChipData }
>(function PriorityChipButton({ priority, className, ...props }, ref) {
  const pinned = pinLabel(priority.priorityOverride);
  const band =
    (priority.priorityOverride as PriorityBand | null) ??
    (priority.priorityScore !== null
      ? priorityBand(priority.priorityScore)
      : null);

  if (!band && !pinned) return null;

  const isP1 = band === "P1" || priority.priorityOverride === "P1";

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex h-5 items-center gap-1 px-1.5 py-0.5 rounded-sm text-xs font-mono font-semibold transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0",
        isP1
          ? "bg-foreground text-background shadow-xs hover:bg-foreground/90"
          : "bg-muted text-foreground border border-border/80 hover:bg-muted/80",
        className,
      )}
      aria-label={
        pinned
          ? `${pinned}. Priority breakdown`
          : band
            ? `${band} priority (score ${priority.priorityScore}). Priority breakdown`
            : "Priority breakdown"
      }
      title={
        priority.priorityScore !== null
          ? `Priority score: ${priority.priorityScore}${pinned ? ` (${pinned})` : ""} · Click to view breakdown`
          : undefined
      }
      {...props}
    >
      <span>{pinned ?? band ?? "Score"}</span>
      <ChevronDown className="size-3 shrink-0 opacity-60" aria-hidden="true" />
    </button>
  );
});

interface PriorityChipProps {
  canonicalId: string;
  priority: PriorityChipData;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Priority chip that opens the breakdown popover (also bound to `w`). */
export function PriorityChip({
  canonicalId,
  priority,
  open,
  onOpenChange,
}: PriorityChipProps) {
  // Praise and irrelevant findings are not ranked: nothing to explain.
  if (priority.priorityScore === null && !priority.priorityOverride)
    return null;
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <PriorityChipButton
          priority={priority}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            // Keep Enter / Space from the row; queue keys still bubble.
            if (e.key === "Enter" || e.key === " ") e.stopPropagation();
          }}
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-96"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key !== "Escape") e.stopPropagation();
        }}
      >
        <PriorityExplanation canonicalId={canonicalId} />
      </PopoverContent>
    </Popover>
  );
}

/** Fetches and renders the stored breakdown of one canonical. */
export function PriorityExplanation({ canonicalId }: { canonicalId: string }) {
  const { data, isLoading } = trpc.finding.explainPriority.useQuery({
    id: canonicalId,
  });
  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.breakdown) {
    return (
      <p className="text-sm text-muted-foreground">
        Only problem findings have a priority.
      </p>
    );
  }
  return (
    <PriorityBreakdownView
      breakdown={data.breakdown}
      weightsLabel={data.weightsLabel}
      override={data.override}
    />
  );
}

interface BreakdownViewProps {
  breakdown: PriorityBreakdown;
  weightsLabel: string;
  override: {
    band: string;
    note: string | null;
    byName: string | null;
  } | null;
}

export function PriorityBreakdownView({
  breakdown,
  weightsLabel,
  override,
}: BreakdownViewProps) {
  const shown = breakdown.factors.filter((f) => f.points > 0);
  const max = Math.max(1, ...shown.map((f) => f.points));
  return (
    <section aria-label="Why it ranks here" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        {override && (
          <p className="text-base font-semibold">
            {pinLabel(override.band)}{" "}
            <span className="text-sm font-normal text-muted-foreground">
              by {override.byName ?? "a teammate"}
            </span>
          </p>
        )}
        <p className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
          Score {breakdown.total} · {weightsLabel} weights
        </p>
      </div>
      {override?.note && (
        <p className="text-sm text-muted-foreground">
          Why pinned: {override.note}
        </p>
      )}
      <dl className="space-y-2">
        {shown.map((f) => (
          <div key={f.key} className="grid grid-cols-[6.5rem_2rem_1fr] gap-2">
            <dt className="text-sm font-medium">{FACTOR_LABEL[f.key]}</dt>
            <dd className="text-right text-sm font-semibold tabular-nums">
              {f.points}
            </dd>
            <dd className="min-w-0 space-y-1">
              <span
                aria-hidden="true"
                className="block h-1.5 rounded-sm bg-mark-important/70"
                style={{ width: `${(f.points / max) * 100}%` }}
              />
              <span className="block text-sm text-muted-foreground">
                {f.reason}
              </span>
            </dd>
          </div>
        ))}
        {breakdown.multipliers.map((m) => (
          <div key={m.key} className="grid grid-cols-[6.5rem_2rem_1fr] gap-2">
            <dt className="text-sm font-medium">
              × {m.key === "criticality" ? "Criticality" : "Confidence"}
            </dt>
            <dd className="text-right text-sm tabular-nums">{m.value}</dd>
            <dd className="text-sm text-muted-foreground">{m.reason}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
